import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { devLog } from '../lib/log';
import { supabase } from '../lib/supabase';
import { getProfile, createProfile, updateProfile } from '../lib/database';
import { readPendingConsent, clearPendingConsent } from '../lib/legal';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refs to access current values in callbacks without causing re-renders
  const userRef = useRef(null);
  const profileRef = useRef(null);
  // Flag: true ONLY when user explicitly clicks "Esci"
  const isExplicitSignOutRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
    profileRef.current = profile;
  }, [user, profile]);

  // Helper: check if there's a valid Supabase token in localStorage
  const hasStoredToken = () => {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('supabase') && key.includes('auth')) {
          const value = localStorage.getItem(key);
          if (value && value.includes('access_token')) {
            return true;
          }
        }
      }
    } catch (e) {
      console.error('Error checking stored token:', e);
    }
    return false;
  };

  useEffect(() => {
    // Controlla sessione iniziale
    checkUser();

    // Handle visibility change (app returning from background)
    let lastVisibleTime = Date.now();
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        lastVisibleTime = Date.now();
        return;
      }

      // App became visible
      const timeInBackground = Date.now() - lastVisibleTime;
      /*#__PURE__*/ devLog('🔷 [AUTH] App became visible after', Math.round(timeInBackground / 1000), 'seconds');

      // If was in background for more than 30 seconds, proactively refresh session
      // iOS PWA can lose connection quickly, so we use a short threshold
      if (timeInBackground > 30 * 1000) {
        /*#__PURE__*/ devLog('🔷 [AUTH] Proactively refreshing session...');
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (error) {
            console.warn('🔷 [AUTH] Session refresh failed:', error.message);
          } else if (data?.session) {
            /*#__PURE__*/ devLog('🔷 [AUTH] Session refreshed successfully');
            setUser(data.session.user);
            // Ensure profile is still loaded
            if (!profileRef.current) {
              await loadProfile(data.session.user.id, data.session.user.user_metadata, data.session.user.email, data.session.access_token);
            }
          }
        } catch (e) {
          console.warn('🔷 [AUTH] Session refresh error:', e.message);
        }
      }

      setLoading(false);
    };
    // Listen to both visibilitychange and pageshow (iOS Safari fires pageshow on BFCache restore)
    const handlePageShow = (e) => {
      if (e.persisted) {
        /*#__PURE__*/ devLog('🔷 [AUTH] pageshow (persisted) — triggering visibility handler');
        handleVisibilityChange();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    // Ascolta cambiamenti auth
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        /*#__PURE__*/ devLog('🔷 [AUTH] Auth state change:', event, '| hasSession:', !!session);

        // Use refs to get current values without causing re-renders
        const currentUser = userRef.current;
        const currentProfile = profileRef.current;

        // Handle different auth events appropriately
        switch (event) {
          case 'SIGNED_OUT':
            // Only clear state if user EXPLICITLY clicked "Esci"
            if (isExplicitSignOutRef.current) {
              /*#__PURE__*/ devLog('🔷 [AUTH] Explicit sign out — clearing state');
              isExplicitSignOutRef.current = false;
              setUser(null);
              setProfile(null);
              setLoading(false);
              return;
            }

            // Automatic SIGNED_OUT (e.g. iOS background token expiry)
            // Try to recover the session before giving up
            /*#__PURE__*/ devLog('🔷 [AUTH] Automatic SIGNED_OUT detected — attempting recovery...');
            try {
              const { data: recoveryData, error: recoveryError } = await supabase.auth.refreshSession();
              if (!recoveryError && recoveryData?.session) {
                /*#__PURE__*/ devLog('🔷 [AUTH] Session recovered successfully after SIGNED_OUT!');
                setUser(recoveryData.session.user);
                // Profile should still be in state; if not, reload it
                if (!profileRef.current) {
                  await loadProfile(recoveryData.session.user.id, recoveryData.session.user.user_metadata, recoveryData.session.user.email, recoveryData.session.access_token);
                }
                setLoading(false);
                return;
              }
              console.warn('🔷 [AUTH] Recovery failed:', recoveryError?.message || 'no session returned');
            } catch (recoveryEx) {
              console.warn('🔷 [AUTH] Recovery exception:', recoveryEx.message);
            }

            // Recovery failed — truly signed out
            /*#__PURE__*/ devLog('🔷 [AUTH] Recovery failed, clearing state');
            setUser(null);
            setProfile(null);
            setLoading(false);
            break;

          case 'TOKEN_REFRESHED':
            // CRITICAL: Don't reset state on token refresh!
            /*#__PURE__*/ devLog('🔷 [AUTH] Token refreshed, keeping current state');
            if (session?.user) {
              setUser(session.user);
            }
            setLoading(false);
            break;

          case 'SIGNED_IN':
          case 'INITIAL_SESSION':
            /*#__PURE__*/ devLog('🔷 [AUTH] User signed in or initial session');
            try {
              if (session?.user) {
                setUser(session.user);
                // Only load profile if we don't have one
                // Pass access_token directly to avoid deadlock with getSession() inside the auth lock
                if (!currentProfile) {
                  await loadProfile(session.user.id, session.user.user_metadata, session.user.email, session.access_token);
                }
              } else if (!currentUser && !currentProfile) {
                // Only reset if we truly have nothing
                setUser(null);
                setProfile(null);
              }
            } catch (error) {
              console.error('❌ [AUTH] Error loading profile on sign in:', error);
              // NEVER reset if we already have valid state
              if (!currentProfile && !currentUser) {
                setUser(null);
                setProfile(null);
              }
            } finally {
              setLoading(false);
            }
            break;

          default:
            // For any other events, be VERY conservative
            /*#__PURE__*/ devLog('🔷 [AUTH] Unhandled auth event:', event);
            // NEVER reset existing valid state
            if (session?.user && !currentUser) {
              setUser(session.user);
              try {
                if (!currentProfile) {
                  await loadProfile(session.user.id, session.user.user_metadata, session.user.email, session.access_token);
                }
              } catch (error) {
                console.error('❌ [AUTH] Error in default handler:', error);
              }
            }
            setLoading(false);
            break;
        }
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []); // Empty deps - only run once on mount

  const checkUser = async () => {
    try {
      /*#__PURE__*/ devLog('🔷 [AUTH] checkUser called');

      // Aggiungi timeout per evitare blocchi infiniti
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('getSession timeout')), 10000)
      );

      const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]);

      if (error) {
        console.error('🔷 [AUTH] Session error:', error);
        // DON'T reset if we have stored token - might be temporary network issue
        if (!hasStoredToken()) {
          setUser(null);
          setProfile(null);
        }
        return;
      }

      /*#__PURE__*/ devLog('🔷 [AUTH] Current session:', !!session);

      if (session?.user) {
        /*#__PURE__*/ devLog('🔷 [AUTH] Session found, user ID:', session.user.id);
        setUser(session.user);
        await loadProfile(session.user.id, session.user.user_metadata, session.user.email, session.access_token);
      } else {
        // Only reset if no stored token
        if (!hasStoredToken()) {
          /*#__PURE__*/ devLog('🔷 [AUTH] No session and no stored token, clearing state');
          setUser(null);
          setProfile(null);
        } else {
          /*#__PURE__*/ devLog('🔷 [AUTH] No session but stored token exists - keeping state');
        }
      }
    } catch (error) {
      console.error('❌ [AUTH] Error checking user:', error);
      // DON'T reset on errors if we have stored token
      if (!hasStoredToken()) {
        setUser(null);
        setProfile(null);
      } else {
        /*#__PURE__*/ devLog('🔷 [AUTH] Error but stored token exists - keeping state');
      }
    } finally {
      /*#__PURE__*/ devLog('🔷 [AUTH] checkUser complete, setting loading to false');
      setLoading(false);
    }
  };

  const loadProfile = async (userId, userMetadata = null, userEmail = null, accessToken = null) => {
    try {
      /*#__PURE__*/ devLog('🔷 [AUTH] loadProfile called for userId:', userId);
      /*#__PURE__*/ devLog('🔷 [AUTH] userMetadata passed:', userMetadata);
      // Merge email: prefer direct email over metadata
      const effectiveEmail = userEmail || userMetadata?.email || null;

      let profileData = await getProfile(userId, accessToken);
      /*#__PURE__*/ devLog('🔷 [AUTH] Profile data received:', profileData);

      if (profileData) {
        /*#__PURE__*/ devLog('🔷 [AUTH] Profile found, role:', profileData.role);

        // Fix: ensure email is saved in profile (backfill for existing users)
        if (!profileData.email && effectiveEmail) {
          /*#__PURE__*/ devLog('🔷 [AUTH] Backfilling email in profile:', effectiveEmail);
          try {
            await updateProfile(userId, { email: effectiveEmail });
            profileData.email = effectiveEmail;
          } catch (e) {
            console.warn('⚠️ [AUTH] Could not backfill email:', e.message);
          }
        }

        // PB-002: utente OAuth — il trigger crea il profilo SENZA consenso (i
        // campi non passano dai metadata). Se c'è un consenso "pending" (spuntato
        // prima del redirect verso il provider), scrivilo ora sul profilo.
        if (!profileData.terms_accepted_at) {
          const pending = readPendingConsent();
          if (pending) {
            try {
              await updateProfile(userId, pending);
              profileData = { ...profileData, ...pending };
            } catch (e) {
              console.warn('⚠️ [AUTH] Could not write pending consent:', e.message);
            }
            clearPendingConsent();
          }
        }

        setProfile(profileData);
      } else {
        console.warn('⚠️ [AUTH] Profile not found for user:', userId);
        console.warn('⚠️ [AUTH] Trigger may have failed - creating profile manually...');

        // Usa i metadata passati invece di chiamare getUser() che si blocca
        const name = userMetadata?.name || 'Utente';
        const email = effectiveEmail;
        // Converti il ruolo da italiano a inglese per il database
        const roleFromMetadata = userMetadata?.role || 'athlete';
        const role = roleFromMetadata === 'atleta' ? 'athlete'
          : roleFromMetadata === 'coach' ? 'coach'
          : roleFromMetadata === 'admin' ? 'admin'
          : 'athlete';

        /*#__PURE__*/ devLog('🔷 [AUTH] Creating profile with name:', name, 'role:', role, 'email:', email);

        // Consenso/accettazione documenti dai metadata di registrazione
        const consent = userMetadata ? {
          privacy_read_at: userMetadata.privacy_read_at,
          privacy_version: userMetadata.privacy_version,
          terms_accepted_at: userMetadata.terms_accepted_at,
          terms_version: userMetadata.terms_version,
        } : null;

        // Crea il profilo
        try {
          profileData = await createProfile(userId, name, role, email, accessToken, consent);
          /*#__PURE__*/ devLog('✅ [AUTH] Profile created successfully:', profileData);
          setProfile(profileData);
        } catch (createError) {
          console.error('❌ [AUTH] Failed to create profile:', createError);
          console.error('❌ [AUTH] This might be an RLS issue - check INSERT policy on profiles');
          setProfile(null);
        }
      }
    } catch (error) {
      console.error('❌ [AUTH] Error loading profile:', error);
      console.error('❌ [AUTH] Error message:', error.message);
      console.error('❌ [AUTH] This might be a database/RLS issue');
      // In caso di errore, settiamo profile a null invece di lasciarlo in loading
      setProfile(null);
    } finally {
      // Assicurati SEMPRE che loading venga settato a false
      /*#__PURE__*/ devLog('🔷 [AUTH] loadProfile complete, setting loading to false');
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      /*#__PURE__*/ devLog('🔷 [AUTH] signIn called for email:', email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      /*#__PURE__*/ devLog('🔷 [AUTH] signInWithPassword response - data:', data);
      /*#__PURE__*/ devLog('🔷 [AUTH] signInWithPassword response - error:', error);

      if (error) {
        console.error('🔷 [AUTH] Supabase auth error:', error);
        throw error;
      }

      /*#__PURE__*/ devLog('🔷 [AUTH] signIn successful, returning data');
      return data;
    } catch (error) {
      console.error('❌ [AUTH] SignIn error:', error);
      console.error('❌ [AUTH] Error type:', error.constructor.name);
      console.error('❌ [AUTH] Error message:', error.message);
      throw error;
    }
  };

  // PB-002 — Login con Google (OAuth). Il consenso Privacy/Termini va spuntato
  // e persistito (markPendingConsent) PRIMA di chiamare questa funzione: lo stato
  // del form si perde nel redirect. redirectTo = origin → si torna sul Login, che
  // reindirizza a /athlete quando profilo e sessione sono pronti.
  const signInWithGoogle = async () => {
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  };

  const signUp = async (email, password, name, role, consent = null) => {
    try {
      /*#__PURE__*/ devLog('🔷 [AUTH] signUp called');
      /*#__PURE__*/ devLog('🔷 [AUTH] Email:', email);
      /*#__PURE__*/ devLog('🔷 [AUTH] Name:', name);
      /*#__PURE__*/ devLog('🔷 [AUTH] Role:', role);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            role,
            email,
            // Accettazione documenti registrata alla sottoscrizione (Privacy=presa
            // visione, Termini=accettazione), scritta nel profilo alla sua creazione.
            ...(consent || {})
          }
        }
      });

      /*#__PURE__*/ devLog('🔷 [AUTH] signUp response - data:', data);
      /*#__PURE__*/ devLog('🔷 [AUTH] signUp response - error:', error);

      if (error) {
        console.error('🔷 [AUTH] Supabase signup error:', error);
        throw error;
      }

      /*#__PURE__*/ devLog('🔷 [AUTH] signUp successful, returning data');
      return data;
    } catch (error) {
      console.error('❌ [AUTH] SignUp error:', error);
      console.error('❌ [AUTH] Error type:', error.constructor.name);
      console.error('❌ [AUTH] Error message:', error.message);
      throw error;
    }
  };

  const signOut = async () => {
    /*#__PURE__*/ devLog('🔷 [AUTH] signOut called (explicit)');

    // Mark as explicit so onAuthStateChange knows not to try recovery
    isExplicitSignOutRef.current = true;

    // Pulisci SEMPRE lo stato locale immediatamente
    setUser(null);
    setProfile(null);

    try {
      // Prova a chiamare Supabase signOut, ma con timeout
      const signOutPromise = supabase.auth.signOut();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('SignOut timeout')), 2000)
      );

      await Promise.race([signOutPromise, timeoutPromise])
        .then(({ error }) => {
          if (error) {
            console.error('❌ [AUTH] Supabase signOut error:', error);
          } else {
            /*#__PURE__*/ devLog('✅ [AUTH] Supabase signOut successful');
          }
        })
        .catch((err) => {
          console.warn('⚠️ [AUTH] Supabase signOut failed or timed out:', err.message);
        });
    } catch (error) {
      console.error('❌ [AUTH] signOut exception:', error);
    } finally {
      // Pulisci localStorage manualmente per sicurezza
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.includes('supabase')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        /*#__PURE__*/ devLog('✅ [AUTH] localStorage cleaned');
      } catch (e) {
        console.error('❌ [AUTH] Could not clean localStorage:', e);
      }
    }
  };

  // Invia l'email di reset password (link -> /reset-password)
  const resetPassword = async (email) => {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
    return true;
  };

  // Imposta la nuova password (usato nella pagina /reset-password durante la recovery)
  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    profile,
    setProfile,
    loading,
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    isCoach: profile?.role === 'coach',
    isAthlete: profile?.role === 'athlete',
    isAdmin: profile?.role === 'admin',
    // Capability profilo unico: può usare le funzioni coach se ha attivato la
    // Modalità Coach, o se è un coach "classico"/admin (retro-compatibilità).
    canCoach: !!(profile?.coach_mode || profile?.role === 'coach' || profile?.role === 'admin'),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
